import React, { useState } from 'react';
import { Box, Button, Modal, Backdrop, Fade, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import ImageIcon from '@mui/icons-material/Image';
import IIndexBase from '../../models/Indexes/iIndexBase';

interface ProductReferenceCellProps {
  row: IIndexBase;
  imageUrl?: string;
}

const ProductReferenceCell: React.FC<ProductReferenceCellProps> = ({ row, imageUrl }) => {
  const { bradyIdUrl } = row;
  const [modalOpen, setModalOpen] = useState(false);

  const getImageTooltip = (bradyUrl: string) => {
    try {
      const url = new URL(bradyUrl);
      return url.hostname ? 'View this product on ' + url.hostname : 'View this product in a new tab';
    } catch {
      return 'View this product in a new tab';
    }
  };

  const handleLinkClick = (url: string) => {
    window.open(url, '_blank');
  };

  const handleImageClick = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  // No image or URL available
  if (!imageUrl && !bradyIdUrl) {
    return (
      <Box component="span" title="No additional details available">
        <DoNotDisturbIcon sx={{ color: 'text.disabled', marginLeft: '8px' }} />
      </Box>
    );
  }

  // Static image only (no Brady URL)
  if (!bradyIdUrl) {
    return (
      <>
        <img
          src={imageUrl}
          alt="Product"
          title="Click to view full size image"
          style={{
            maxHeight: '100%',
            maxWidth: '100%',
            objectFit: 'contain',
            cursor: 'pointer',
          }}
          onClick={handleImageClick}
        />
        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
            },
          }}
          aria-labelledby="image-modal-title"
          aria-describedby="image-modal-description"
        >
          <Fade in={modalOpen}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 2,
                maxWidth: '90vw',
                maxHeight: '90vh',
                outline: 'none',
                display: 'flex',
                flexDirection: 'column',
                border: '2px solid #000',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 1,
                }}
              >
                <IconButton
                  onClick={handleModalClose}
                  sx={{ position: 'absolute', right: '8px', top: '8px' }}
                  aria-label="Close image popup"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <img
                src={imageUrl}
                alt="Product - Full Size"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Fade>
        </Modal>
      </>
    );
  }

  const tooltip = getImageTooltip(bradyIdUrl);

  // Interactive image or icon button
  return imageUrl ? (
    <img
      src={imageUrl}
      alt="Product"
      style={{
        maxHeight: '100%',
        maxWidth: '100%',
        objectFit: 'contain',
        cursor: 'pointer',
      }}
      title={tooltip}
      onClick={() => handleLinkClick(bradyIdUrl)}
    />
  ) : (
    <Button
      title={tooltip}
      aria-label={tooltip}
      onClick={() => handleLinkClick(bradyIdUrl)}
      sx={{
        minWidth: 'auto',
        padding: '8px',
        color: 'text.disabled',
      }}
    >
      <ImageIcon />
    </Button>
  );
};

export default ProductReferenceCell;
